import React, { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { INTERVIEW_TYPES, INTERVIEW_MODES } from '../../utils/hirelaneInterviewConstants';

function localDatetimeToIso(dateStr, timeStr) {
  if (!dateStr || !timeStr) return '';
  const d = new Date(`${dateStr}T${timeStr}`);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

/** Prefill date/time from ISO string for datetime-local style inputs */
function isoToDateParts(iso) {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  const pad = (n) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`
  };
}

function initialAttendeesFromInterview(initial) {
  const arr = initial?.interview_attendees;
  if (Array.isArray(arr) && arr.length > 0) {
    return arr.map((a, i) => ({
      id: `${i}-${a.hirelane_interviewer_id || ''}-${a.email || ''}`,
      name: (a.name || '').trim(),
      email: (a.email || '').trim(),
      hirelane_interviewer_id: a.hirelane_interviewer_id || null
    }));
  }
  if ((initial?.interviewer_name || '').trim() || (initial?.interviewer_email || '').trim()) {
    return [
      {
        id: 'legacy',
        name: (initial.interviewer_name || '').trim(),
        email: (initial.interviewer_email || '').trim(),
        hirelane_interviewer_id: null
      }
    ];
  }
  return [];
}

export default function ScheduleInterviewModal({
  candidateName,
  existingInterview,
  duplicateExisting,
  predefinedInterviewers = [],
  onClose,
  onSubmit,
  submitting
}) {
  const isEdit = !!existingInterview;
  const initial = existingInterview || {};

  const parts = useMemo(() => isoToDateParts(initial.scheduled_at), [initial.scheduled_at]);

  const [interview_type, setInterviewType] = useState(initial.interview_type || 'Technical');
  const [dateStr, setDateStr] = useState(parts.date);
  const [timeStr, setTimeStr] = useState(parts.time);
  const [duration_minutes, setDurationMinutes] = useState(
    initial.duration_minutes != null ? String(initial.duration_minutes) : '60'
  );
  const [interview_mode, setInterviewMode] = useState(initial.interview_mode || 'Online');
  const [meeting_link, setMeetingLink] = useState(initial.meeting_link || '');
  const [location, setLocation] = useState(initial.location || '');
  const [attendees, setAttendees] = useState(() => initialAttendeesFromInterview(initial));
  const [pickId, setPickId] = useState('');
  const [customName, setCustomName] = useState('');
  const [customEmail, setCustomEmail] = useState('');
  const [notes, setNotes] = useState(initial.notes || '');
  const [create_google_calendar_event, setCreateGoogleCalendarEvent] = useState(
    (initial.interview_mode || 'Online') === 'Online'
  );
  const [send_calendar_invite_emails, setSendCalendarInviteEmails] = useState(false);

  const availablePredefined = useMemo(() => {
    const taken = new Set(attendees.map((a) => a.hirelane_interviewer_id).filter(Boolean));
    return (predefinedInterviewers || []).filter((p) => p?.id && !taken.has(p.id));
  }, [predefinedInterviewers, attendees]);

  const addFromDirectory = () => {
    if (!pickId) return;
    const p = (predefinedInterviewers || []).find((x) => x.id === pickId);
    if (!p) return;
    setAttendees((prev) => [
      ...prev,
      {
        id: `dir-${p.id}-${Date.now()}`,
        name: (p.display_name || '').trim(),
        email: (p.email || '').trim(),
        hirelane_interviewer_id: p.id
      }
    ]);
    setPickId('');
  };

  const addCustom = () => {
    const n = customName.trim();
    const e = customEmail.trim();
    if (!n && !e) return;
    setAttendees((prev) => [
      ...prev,
      { id: `cust-${Date.now()}`, name: n, email: e, hirelane_interviewer_id: null }
    ]);
    setCustomName('');
    setCustomEmail('');
  };

  const removeAttendee = (id) => setAttendees((prev) => prev.filter((a) => a.id !== id));

  const updateAttendee = (id, field, raw) => {
    const value = typeof raw === 'string' ? raw : '';
    setAttendees((prev) =>
      prev.map((a) => {
        if (a.id !== id) return a;
        const next = { ...a, [field]: value };
        if (field === 'name' || field === 'email') next.hirelane_interviewer_id = null;
        return next;
      })
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const scheduled_at = localDatetimeToIso(dateStr, timeStr);
    if (!scheduled_at) return;
    const dm = parseInt(duration_minutes, 10);
    const interview_attendees = attendees
      .map((a) => ({
        name: a.name.trim() || null,
        email: a.email.trim() || null,
        hirelane_interviewer_id: a.hirelane_interviewer_id || null
      }))
      .filter((a) => a.name || a.email);
    const payload = isEdit
      ? {
          scheduled_at,
          duration_minutes: Number.isFinite(dm) ? dm : null,
          interview_mode,
          meeting_link: meeting_link.trim() || null,
          location: location.trim() || null,
          interview_attendees,
          notes: notes.trim() || null
        }
      : {
          interview_type,
          scheduled_at,
          duration_minutes: Number.isFinite(dm) ? dm : null,
          interview_mode,
          meeting_link: meeting_link.trim() || null,
          location: location.trim() || null,
          interview_attendees,
          notes: notes.trim() || null
        };
    if (interview_mode === 'Online' && create_google_calendar_event) {
      payload.create_google_calendar_event = true;
      payload.send_calendar_invite_emails = !!send_calendar_invite_emails;
    }
    onSubmit(payload);
  };

  return (
    <>
      <div className="hirelane-modal-backdrop" onClick={onClose} aria-hidden />
      <div className="hirelane-modal hirelane-modal--wide" role="dialog" aria-modal="true" aria-label="Schedule interview">
        <div className="hirelane-modal__header">
          <div style={{ fontWeight: 700, fontSize: 16 }}>
            {isEdit ? 'Reschedule interview' : 'Schedule interview'} — {candidateName}
          </div>
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
        <form className="hirelane-modal__body" onSubmit={handleSubmit}>
          {duplicateExisting && !isEdit ? (
            <div
              style={{
                marginBottom: 12,
                padding: 10,
                borderRadius: 6,
                background: '#fff7ed',
                border: '1px solid #fdba74',
                fontSize: 13
              }}
            >
              This candidate already has an active scheduled interview ({duplicateExisting.display_round_label}). You can
              still schedule another after confirming in the parent dialog, or reschedule the existing one from the
              Interview Scheduled tab.
            </div>
          ) : null}

          {!isEdit ? (
            <label className="hirelane-detail__statusDecisionLabel">
              Interview type
              <select
                className="hirelane-detail__statusDecisionSelect"
                value={interview_type}
                onChange={(e) => setInterviewType(e.target.value)}
                required
              >
                {INTERVIEW_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <div style={{ marginBottom: 10, fontSize: 14 }}>
              <strong>{existingInterview.display_round_label}</strong>
              <span style={{ opacity: 0.75, marginLeft: 8 }}>(round cannot be changed here)</span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label className="hirelane-detail__statusDecisionLabel">
              Date
              <input
                type="date"
                className="hirelane-detail__statusDecisionSelect"
                value={dateStr}
                onChange={(e) => setDateStr(e.target.value)}
                required
              />
            </label>
            <label className="hirelane-detail__statusDecisionLabel">
              Time
              <input
                type="time"
                className="hirelane-detail__statusDecisionSelect"
                value={timeStr}
                onChange={(e) => setTimeStr(e.target.value)}
                required
              />
            </label>
          </div>

          <label className="hirelane-detail__statusDecisionLabel">
            Duration (minutes)
            <input
              type="number"
              min={15}
              step={5}
              className="hirelane-detail__statusDecisionSelect"
              value={duration_minutes}
              onChange={(e) => setDurationMinutes(e.target.value)}
            />
          </label>

          <label className="hirelane-detail__statusDecisionLabel">
            Interview mode
            <select
              className="hirelane-detail__statusDecisionSelect"
              value={interview_mode}
              onChange={(e) => {
                const v = e.target.value;
                setInterviewMode(v);
                if (v !== 'Online') {
                  setCreateGoogleCalendarEvent(false);
                  setSendCalendarInviteEmails(false);
                } else {
                  setCreateGoogleCalendarEvent(true);
                  setSendCalendarInviteEmails(false);
                }
              }}
              required
            >
              {INTERVIEW_MODES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>

          <label
            className="hirelane-detail__statusDecisionLabel"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              cursor: interview_mode === 'Online' ? 'pointer' : 'not-allowed',
              opacity: interview_mode === 'Online' ? 1 : 0.55
            }}
          >
            <input
              type="checkbox"
              checked={interview_mode === 'Online' && create_google_calendar_event}
              disabled={interview_mode !== 'Online'}
              onChange={(e) => {
                const on = e.target.checked;
                setCreateGoogleCalendarEvent(on);
                if (!on) setSendCalendarInviteEmails(false);
              }}
              style={{ marginTop: 3 }}
            />
            <span>Create Google Calendar invite with Meet</span>
          </label>

          <label
            className="hirelane-detail__statusDecisionLabel"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              marginLeft: 22,
              cursor:
                interview_mode === 'Online' && create_google_calendar_event ? 'pointer' : 'not-allowed',
              opacity: interview_mode === 'Online' && create_google_calendar_event ? 1 : 0.55
            }}
          >
            <input
              type="checkbox"
              checked={!!send_calendar_invite_emails}
              disabled={interview_mode !== 'Online' || !create_google_calendar_event}
              onChange={(e) => setSendCalendarInviteEmails(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span>Email calendar invitations to attendees now (candidate, interviewers on the event)</span>
          </label>

          <label className="hirelane-detail__statusDecisionLabel">
            Meeting link (online)
            <input
              className="hirelane-detail__statusDecisionSelect"
              value={meeting_link}
              onChange={(e) => setMeetingLink(e.target.value)}
              placeholder="https://…"
            />
          </label>
          <label className="hirelane-detail__statusDecisionLabel">
            Location (in person)
            <input
              className="hirelane-detail__statusDecisionSelect"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Office / address"
            />
          </label>

          <div
            style={{
              marginTop: 4,
              marginBottom: 8,
              padding: 12,
              borderRadius: 6,
              border: '1px solid #e2e8f0',
              background: '#f8fafc'
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Interviewers</div>
            <p style={{ margin: '0 0 10px', fontSize: 12, opacity: 0.85, lineHeight: 1.45 }}>
              Add one or more people from your saved directory or as custom guests. For calendar invites, each guest
              should have a valid email. Manage the directory from the Hirelane dashboard (Manage interviewers).
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', marginBottom: 10 }}>
              <label className="hirelane-detail__statusDecisionLabel" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                From directory
                <select
                  className="hirelane-detail__statusDecisionSelect"
                  value={pickId}
                  onChange={(e) => setPickId(e.target.value)}
                >
                  <option value="">Choose…</option>
                  {availablePredefined.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.display_name} ({p.email})
                    </option>
                  ))}
                </select>
              </label>
              <Button type="button" variant="secondary" onClick={addFromDirectory} disabled={!pickId}>
                Add
              </Button>
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Custom guest</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'end' }}>
              <label className="hirelane-detail__statusDecisionLabel" style={{ marginBottom: 0 }}>
                Name
                <input
                  className="hirelane-detail__statusDecisionSelect"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder="Optional if email set"
                />
              </label>
              <label className="hirelane-detail__statusDecisionLabel" style={{ marginBottom: 0 }}>
                Email
                <input
                  type="email"
                  className="hirelane-detail__statusDecisionSelect"
                  value={customEmail}
                  onChange={(e) => setCustomEmail(e.target.value)}
                  placeholder="name@company.com"
                />
              </label>
              <Button type="button" variant="secondary" onClick={addCustom}>
                Add
              </Button>
            </div>
            {attendees.length > 0 ? (
              <ul style={{ margin: '12px 0 0', padding: 0, listStyle: 'none' }}>
                {attendees.map((a) => (
                  <li
                    key={a.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr auto',
                      gap: 8,
                      alignItems: 'center',
                      padding: '8px 0',
                      borderTop: '1px solid #e2e8f0'
                    }}
                  >
                    <input
                      className="hirelane-detail__statusDecisionSelect"
                      value={a.name}
                      onChange={(e) => updateAttendee(a.id, 'name', e.target.value)}
                      placeholder="Name"
                      aria-label="Interviewer name"
                    />
                    <input
                      type="email"
                      className="hirelane-detail__statusDecisionSelect"
                      value={a.email}
                      onChange={(e) => updateAttendee(a.id, 'email', e.target.value)}
                      placeholder="Email"
                      aria-label="Interviewer email"
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                      {a.hirelane_interviewer_id ? (
                        <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>Directory</span>
                      ) : (
                        <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>Custom</span>
                      )}
                      <Button type="button" variant="secondary" onClick={() => removeAttendee(a.id)}>
                        Remove
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ margin: '10px 0 0', fontSize: 12, opacity: 0.75 }}>No interviewers listed yet (optional).</p>
            )}
          </div>

          <label className="hirelane-detail__statusDecisionLabel">
            Notes
            <textarea
              className="hirelane-detail__statusDecisionTextarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </label>

          <div className="hirelane-detail__statusDecisionActions">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : isEdit ? 'Save reschedule' : 'Schedule'}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
